import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Shield,
  HelpCircle,
  LogOut,
  ChevronRight,
  Sun,
  Moon,
  Loader2,
} from 'lucide-react';
import { AppLayout } from '../../components/AppLayout';
import { Card, Button, Select, Checkbox, ThemeToggle, useToast } from '../../components/ui';
import { useTheme, useAuth } from '../../context';
import { userApi } from '../../services';
import './Settings.css';

// Supported languages
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
];

// Common timezones
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'Europe/Istanbul', label: 'Istanbul' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, children }) => (
  <div className="settings-section">
    <h2 className="settings-section__title">{title}</h2>
    <Card className="settings-section__card">{children}</Card>
  </div>
);

interface SettingsItemProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  to?: string;
}

const SettingsItem: React.FC<SettingsItemProps> = ({ icon, label, description, action, onClick, to }) => {
  const content = (
    <div className={`settings-item ${onClick || to ? 'settings-item--clickable' : ''}`} onClick={onClick}>
      <div className="settings-item__icon">{icon}</div>
      <div className="settings-item__content">
        <span className="settings-item__label">{label}</span>
        {description && <span className="settings-item__description">{description}</span>}
      </div>
      <div className="settings-item__action">{action || ((onClick || to) && <ChevronRight size={20} />)}</div>
    </div>
  );

  if (to) {
    return <Link to={to} className="settings-item-link">{content}</Link>;
  }

  return content;
};

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const { logout } = useAuth();
  const { success, error: showError } = useToast();

  // Profile loading state
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Preferences state
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC');
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  const [notifications, setNotifications] = useState({
    dailyReminder: true,
    weeklyDigest: false,
    achievements: true,
  });

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const data = await userApi.getProfile();
        setLanguage(data.languagePreference || 'en');
        setTimezone(data.timezone || 'UTC');
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle language change
  const handleLanguageChange = async (newLanguage: string) => {
    const previousLanguage = language;
    setLanguage(newLanguage);

    try {
      setIsSavingPreferences(true);
      await userApi.updatePreferences({
        languagePreference: newLanguage,
        timezone: timezone,
      });
      success('Language updated successfully');
    } catch (err) {
      setLanguage(previousLanguage);
      showError('Failed to update language');
      console.error('Error updating language:', err);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Handle timezone change
  const handleTimezoneChange = async (newTimezone: string) => {
    const previousTimezone = timezone;
    setTimezone(newTimezone);

    try {
      setIsSavingPreferences(true);
      await userApi.updatePreferences({
        languagePreference: language,
        timezone: newTimezone,
      });
      success('Timezone updated successfully');
    } catch (err) {
      setTimezone(previousTimezone);
      showError('Failed to update timezone');
      console.error('Error updating timezone:', err);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  return (
    <AppLayout showBackButton backPath="/dashboard" backLabel="Back" pageTitle="Settings">
      <div className="settings-screen">
        {/* Content */}
        <main className="settings-content">
        {/* Profile Section */}
        <SettingsSection title="Profile">
          <SettingsItem
            icon={<User size={20} />}
            label="Edit Profile"
            description="Name, photo, and account details"
            to="/profile"
          />
        </SettingsSection>

        {/* Appearance Section */}
        <SettingsSection title="Appearance">
          <SettingsItem
            icon={isDark ? <Moon size={20} /> : <Sun size={20} />}
            label="Theme"
            description={`Currently using ${theme} mode`}
            action={<ThemeToggle variant="icon" />}
          />
        </SettingsSection>

        {/* Language & Region Section */}
        <SettingsSection title="Language & Region">
          <div className="settings-preferences">
            <div className="settings-select-wrapper">
              <Select
                label="Language"
                options={LANGUAGES}
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                disabled={isSavingPreferences || isLoadingProfile}
              />
              {isSavingPreferences && (
                <Loader2 className="settings-select-spinner" size={16} />
              )}
            </div>
            <div className="settings-select-wrapper">
              <Select
                label="Timezone"
                options={TIMEZONES}
                value={timezone}
                onChange={(e) => handleTimezoneChange(e.target.value)}
                disabled={isSavingPreferences || isLoadingProfile}
              />
            </div>
          </div>
        </SettingsSection>

        {/* Notifications Section */}
        <SettingsSection title="Notifications">
          <div className="settings-notifications">
            <Checkbox
              label="Daily check-in reminder"
              checked={notifications.dailyReminder}
              onChange={(e) => setNotifications({ ...notifications, dailyReminder: e.target.checked })}
              helperText="Gentle nudge to start your creative day"
            />
            <Checkbox
              label="Weekly progress digest"
              checked={notifications.weeklyDigest}
              onChange={(e) => setNotifications({ ...notifications, weeklyDigest: e.target.checked })}
              helperText="Summary of your creative journey"
            />
            <Checkbox
              label="Achievement celebrations"
              checked={notifications.achievements}
              onChange={(e) => setNotifications({ ...notifications, achievements: e.target.checked })}
              helperText="Get notified when you hit milestones"
            />
          </div>
        </SettingsSection>

        {/* Preferences Section */}
        <SettingsSection title="Preferences">
          <div className="settings-preferences">
            <Select
              label="Default project view"
              options={[
                { value: 'board', label: 'Board View' },
                { value: 'list', label: 'List View' },
                { value: 'calendar', label: 'Calendar View' },
              ]}
              defaultValue="board"
            />
            <Select
              label="Week starts on"
              options={[
                { value: 'sunday', label: 'Sunday' },
                { value: 'monday', label: 'Monday' },
              ]}
              defaultValue="monday"
            />
          </div>
        </SettingsSection>

        {/* Support Section */}
        <SettingsSection title="Support">
          <SettingsItem
            icon={<HelpCircle size={20} />}
            label="Help & FAQ"
            onClick={() => {}}
          />
          <SettingsItem
            icon={<Shield size={20} />}
            label="Privacy Policy"
            onClick={() => {}}
          />
        </SettingsSection>

        {/* Account Actions */}
        <div className="settings-actions">
          <Button variant="ghost" fullWidth className="settings-logout" onClick={handleLogout}>
            <LogOut size={18} />
            Sign Out
          </Button>
        </div>

        {/* App Version */}
        <p className="settings-version">Artist Workflow Platform v1.0.0</p>
        </main>
      </div>
    </AppLayout>
  );
};

export default Settings;
